/* assets/gcitypartners-sign-in.js — GCITYPARTNERS-ACCOUNT-SIGN-IN-01
 * Thin presentation consumer of the canonical LittleGWebSession.
 * Owns no auth/session storage and never mutates Provider application/approval.
 */
(function (window, document) {
  'use strict';

  /**
   * @typedef {Readonly<{
   *   ready: () => Promise<unknown>,
   *   getState: () => {status?: string} | null,
   *   requestMagicLink: (
   *     email: string,
   *     options: {client: string, return_to: string}
   *   ) => Promise<unknown>
   * }>} SignInSessionConsumer
   */
  /**
   * @type {Window & {
   *   __GCITYPARTNERS_SIGN_IN_RUNTIME__?: boolean,
   *   LittleGWebSession?: SignInSessionConsumer | null
   * }}
   */
  const signInWindow = window;
  const GLOBAL_KEY = '__GCITYPARTNERS_SIGN_IN_RUNTIME__';
  if (signInWindow[GLOBAL_KEY]) return;
  signInWindow[GLOBAL_KEY] = true;

  var ROOT = '[data-gcitypartners-sign-in]';
  var DASHBOARD = '/pages/gcitypartners-dashboard?source=gcitypartners-sign-in';
  /** @type {WeakSet<Element>} */
  const mounted = new WeakSet();
  /** @type {WeakMap<Element, number>} */
  const redirectTimers = new WeakMap();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function owner() {
    const session = signInWindow.LittleGWebSession;
    return session &&
      typeof session.ready === 'function' &&
      typeof session.getState === 'function' &&
      typeof session.requestMagicLink === 'function'
      ? session
      : null;
  }

  /**
   * @param {Element} root
   * @param {'booting' | 'authenticated' | 'guest' | 'error'} name
   */
  function show(root, name) {
    root.setAttribute('data-gcitypartners-sign-in-state', name);
    root.querySelectorAll('[data-gcitypartners-sign-in-view]').forEach(function (view) {
      if (!(view instanceof HTMLElement)) return;
      view.hidden = view.getAttribute('data-gcitypartners-sign-in-view') !== name;
    });
  }

  /**
   * @param {Element} root
   * @param {string} text
   */
  function message(root, text) {
    var target = root.querySelector('[data-gcitypartners-sign-in-message]');
    if (target) target.textContent = text || '';
  }

  /** @param {unknown} raw */
  function safeReturnPath(raw) {
    var value = clean(raw);
    if (
      !value ||
      value.length > 512 ||
      !/^\/(pages|collections|products)\/[a-z0-9][a-z0-9\-/]*$/i.test(value)
    )
      return '';
    if (value.indexOf('//') !== -1 || value.indexOf('..') !== -1) return '';
    return value;
  }

  function signInReturnTarget() {
    var requested = '';
    try {
      requested = new URLSearchParams(window.location.search || '').get('return_to') || '';
    } catch (_) {}
    return safeReturnPath(requested) || DASHBOARD;
  }

  /** @param {Element} root */
  function scheduleDashboard(root) {
    if (redirectTimers.has(root)) return;
    var timer = window.setTimeout(function () {
      redirectTimers.delete(root);
      if (!root.isConnected) return;
      var session = owner();
      var state = session && session.getState();
      if (!state || state.status !== 'authenticated') return;
      window.location.replace(signInReturnTarget());
    }, 650);
    redirectTimers.set(root, timer);
  }

  /** @param {Element} root */
  function render(root) {
    var session = owner();
    var state = session && session.getState();
    var status = state && state.status;
    if (status === 'authenticated') {
      show(root, 'authenticated');
      scheduleDashboard(root);
      return;
    }
    if (redirectTimers.has(root)) {
      window.clearTimeout(redirectTimers.get(root));
      redirectTimers.delete(root);
    }
    if (status === 'booting' || status === 'authenticating') show(root, 'booting');
    else if (status === 'guest' || status === 'anonymous') show(root, 'guest');
    else show(root, 'error');
  }

  /** @param {Element} root */
  async function resolve(root) {
    show(root, 'booting');
    var session = owner();
    if (!session) {
      show(root, 'error');
      return;
    }

    try {
      await session.ready();
    } catch (_) {
      if (root.isConnected) show(root, 'error');
      return;
    }
    if (!root.isConnected) return;

    render(root);
  }

  /**
   * @param {Element} root
   * @param {string} key
   * @param {string} fallback
   */
  function localizedMessage(root, key, fallback) {
    return clean(root.getAttribute('data-gcp-sign-in-' + key)) || fallback;
  }

  /**
   * @param {Element} root
   * @param {HTMLFormElement} form
   */
  async function requestLink(root, form) {
    var session = owner();
    if (!session) {
      show(root, 'error');
      return;
    }

    const submit = form.querySelector('button[type="submit"]');
    const button = submit instanceof HTMLButtonElement ? submit : null;
    if (button && button.disabled) return;

    const field = form.elements.namedItem('email');
    const email = clean(field instanceof HTMLInputElement ? field.value : '');
    if (button) button.disabled = true;
    message(
      root,
      localizedMessage(root, 'sending', 'Sending your secure GCityPartners sign-in link…')
    );

    try {
      await session.requestMagicLink(email, {
        client: 'gcitypartners_web',
        return_to: safeReturnPath(signInReturnTarget()),
      });
      message(
        root,
        localizedMessage(root, 'sent', 'Check your email for your secure sign-in link.')
      );
    } catch (error) {
      const code = clean(
        error !== null && typeof error === 'object' && 'code' in error ? error.code : undefined
      );
      const status = Number(
        error !== null && typeof error === 'object' && 'status' in error ? error.status : undefined
      );
      const key =
        status === 429
          ? 'rate-limit'
          : code === 'invalid_email'
            ? 'invalid-email'
            : code === 'magic_link_timeout' || code === 'request_timeout'
              ? 'timeout'
              : 'request-failed';
      message(root, localizedMessage(root, key, 'The sign-in link could not be sent.'));
    } finally {
      if (button) button.disabled = false;
    }
  }

  /** @param {Element | null} root */
  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);

    const form = root.querySelector('[data-gcitypartners-sign-in-form]');
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        void requestLink(root, form);
      });
    }

    var retry = root.querySelector('[data-gcitypartners-sign-in-retry]');
    if (retry) {
      retry.addEventListener('click', function () {
        show(root, 'guest');
        message(root, '');
      });
    }

    void resolve(root);
  }

  /** @param {ParentNode | null} [scope] */
  function mountAll(scope) {
    const base = scope || document;
    if (base instanceof Element && base.matches(ROOT)) mount(base);
    base.querySelectorAll(ROOT).forEach(mount);
  }

  document.addEventListener('littleg:web-session-change', function () {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT), function (root) {
      if (mounted.has(root)) render(root);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target instanceof Element ? event.target : document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  } else {
    mountAll(document);
  }
})(window, document);
